import { useState, useEffect, useMemo, useCallback } from "react";
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subDays, isSameMonth, isSameYear } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowDownCircle, ArrowUpCircle, BarChart3, Plus, Wallet, AlertTriangle, TrendingUp, Calendar, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DateRange } from "react-day-picker";

import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BalanceCard } from "@/components/BalanceCard";
import { TransactionCard } from "@/components/TransactionCard";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTransactions } from "@/hooks/useTransactions";
import { useOverdueAccounts } from "@/hooks/useOverdueAccounts";
import { useDashboardData, useDashboardActions } from "@/hooks/useDashboardData";
import { useDashboardMetrics } from "@/hooks/useDashboardMetrics";
import { CashFlowChart } from "@/components/CashFlowChart";
import { UnifiedTransactionForm } from "@/components/transactions/UnifiedTransactionForm";
import { toast } from "sonner";
import { formatCurrency } from '@/lib/utils/currencyUtils';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { fromDateString, toDateString } from "@/lib/utils/dateUtils";

// Date filter presets
type PresetKey = 'this-month' | 'last-month' | 'this-quarter' | 'last-quarter' | 'this-year' | 'last-year' | 'last-30-days' | 'last-90-days' | 'custom';

interface DatePreset {
  label: string;
  getValue: () => { from: Date; to: Date };
}

const getDatePresets = (): Record<PresetKey, DatePreset> => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return {
    'this-month': {
      label: 'Este Mês',
      getValue: () => ({
        from: startOfMonth(today),
        to: endOfMonth(today),
      }),
    },
    'last-month': {
      label: 'Mês Passado',
      getValue: () => ({
        from: startOfMonth(subMonths(today, 1)),
        to: endOfMonth(subMonths(today, 1)),
      }),
    },
    'this-quarter': {
      label: 'Este Trimestre',
      getValue: () => {
        const quarterStart = Math.floor(now.getMonth() / 3) * 3;
        return {
          from: new Date(now.getFullYear(), quarterStart, 1),
          to: new Date(now.getFullYear(), quarterStart + 3, 0),
        };
      },
    },
    'last-quarter': {
      label: 'Trim. Passado',
      getValue: () => {
        const quarterStart = Math.floor(now.getMonth() / 3) * 3 - 3;
        const year = quarterStart < 0 ? now.getFullYear() - 1 : now.getFullYear();
        const adjustedQuarter = quarterStart < 0 ? 9 : quarterStart;
        return {
          from: new Date(year, adjustedQuarter, 1),
          to: new Date(year, adjustedQuarter + 3, 0),
        };
      },
    },
    'this-year': {
      label: 'Este Ano',
      getValue: () => ({
        from: startOfYear(today),
        to: endOfYear(today),
      }),
    },
    'last-year': {
      label: 'Ano Passado',
      getValue: () => {
        const lastYear = new Date(now.getFullYear() - 1, 0, 1);
        return {
          from: startOfYear(lastYear),
          to: endOfYear(lastYear),
        };
      },
    },
    'last-30-days': {
      label: '30 Dias',
      getValue: () => ({
        from: subDays(today, 29),
        to: today,
      }),
    },
    'last-90-days': {
      label: '90 Dias',
      getValue: () => ({
        from: subDays(today, 89),
        to: today,
      }),
    },
    'custom': {
      label: 'Personalizado',
      getValue: () => ({
        from: startOfMonth(today),
        to: endOfMonth(today),
      }),
    },
  };
};

export function Dashboard() {
  // Date filter state - default to current month
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const now = new Date();
    return {
      from: startOfMonth(now),
      to: endOfMonth(now),
    };
  });
  const [selectedPreset, setSelectedPreset] = useState<PresetKey>('this-month');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const [activeTab, setActiveTab] = useState("overview");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState<string | null>(null);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [isLoaded, setIsLoaded] = useState(false);
  const [pageSize] = useState(10);
  const { transactions, isLoading, getTransactionById } = useTransactions();
  const navigate = useNavigate();

  // Format dates for the API
  const startDateStr = useMemo(() => dateRange.from ? toDateString(dateRange.from) : null, [dateRange.from]);
  const endDateStr = useMemo(() => dateRange.to ? toDateString(dateRange.to) : null, [dateRange.to]);

  // Filter transactions by date range for dashboard data
  const filteredTransactions = useMemo(() => {
    if (!transactions || !dateRange.from || !dateRange.to) return transactions;

    return transactions.filter(t => {
      const transactionDate = t.date instanceof Date ? t.date : fromDateString(t.date);
      return transactionDate >= dateRange.from! && transactionDate <= dateRange.to!;
    });
  }, [transactions, dateRange]);

  // Handle preset selection
  const handlePresetChange = useCallback((preset: PresetKey) => {
    const presets = getDatePresets();
    const { from, to } = presets[preset].getValue();
    setDateRange({ from, to });
    setSelectedPreset(preset);
    if (preset !== 'custom') {
      setIsDatePickerOpen(false);
    }
  }, []);

  // Handle custom date selection
  const handleDateRangeChange = useCallback((range: DateRange | undefined) => {
    if (range) {
      setDateRange(range);
      setSelectedPreset('custom');
    }
  }, []);

  // Get display label for the date range
  const dateRangeLabel = useMemo(() => {
    if (!dateRange.from) return 'Selecione um período';

    const presets = getDatePresets();

    // Check if current range matches a preset
    if (selectedPreset !== 'custom') {
      return presets[selectedPreset].label;
    }

    // Format custom range
    if (dateRange.to) {
      if (isSameMonth(dateRange.from, dateRange.to) && isSameYear(dateRange.from, dateRange.to)) {
        return format(dateRange.from, "MMMM 'de' yyyy", { locale: ptBR });
      }
      return `${format(dateRange.from, 'dd/MM/yy', { locale: ptBR })} - ${format(dateRange.to, 'dd/MM/yy', { locale: ptBR })}`;
    }

    return format(dateRange.from, 'dd/MM/yyyy', { locale: ptBR });
  }, [dateRange, selectedPreset]);

  // Phase 3: Use stored procedure for optimized metrics (5x faster)
  const { metrics: storedProcedureMetrics, isLoading: metricsLoading } = useDashboardMetrics({
    startDate: startDateStr || undefined,
    endDate: endDateStr || undefined,
  });

  // Keep transaction-based data for lists and analytics - use filtered transactions
  const dashboardData = useDashboardData(filteredTransactions, isLoading, pageSize);
  const dashboardActions = useDashboardActions();

  // Use stored procedure metrics for balance cards (faster), fallback to calculated metrics
  const metrics = storedProcedureMetrics;

  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => setIsLoaded(true), 300);
    }
  }, [isLoading]);

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingTransactionId(null);
  };

  const handleAddNewTransaction = () => {
    navigate("/transactions");

    // Wait for navigation to complete before dispatching event
    setTimeout(() => {
      const event = new CustomEvent("openNewTransactionDialog");
      window.dispatchEvent(event);
    }, 100);
  };


  const handleGoToReports = () => {
    navigate("/reports");
  };

  // Dados de inadimplência
  const { data: overdueData, summary: overdueSummary } = useOverdueAccounts({
    status: ['atrasado'],
    priority: ['critica', 'alta', 'media', 'baixa']
  });

  const handleSelectTransaction = useCallback((transactionId: string) => {
    setSelectedTransactions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(transactionId)) {
        newSet.delete(transactionId);
      } else {
        newSet.add(transactionId);
      }
      return newSet;
    });
  }, []);

  // Handlers simplificados usando o hook de ações
  const handleEditTransaction = useCallback((transactionId: string) => {
    dashboardActions.handleEditTransaction(transactionId, setEditingTransactionId, setIsDialogOpen);
  }, [dashboardActions]);

  const handleDeleteTransaction = useCallback(async (transactionId: string) => {
    await dashboardActions.handleDeleteTransaction(transactionId);
  }, [dashboardActions]);

  const handleTogglePaid = useCallback(async (transactionId: string) => {
    await dashboardActions.handleTogglePaid(transactionId);
  }, [dashboardActions]);

  return (
    <div className="dashboard-wrapper">
      <div className={`dashboard-container flex flex-col gap-4 md:gap-6 ${isLoaded ? 'animate-fade-in' : 'opacity-0'}`}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="animate-slide-down">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Dashboard
            </h1>
            <p className="text-muted-foreground mt-1 flex items-center gap-2">
              <span className="w-2 h-2 bg-success rounded-full animate-pulse"></span>
              {format(new Date(), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
            </p>
          </div>
          <Button
            onClick={handleGoToReports}
            className="btn-primary dark:bg-accent dark:hover:bg-accent/90 dark:text-accent-foreground group"
            size="lg"
          >
            <BarChart3 className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
            Relatórios
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 [grid-auto-rows:160px]">
          {/* Card de Saldo Atual - Powered by Phase 2 stored procedure */}
          <div className="animate-slide-up" style={{ animationDelay: '0.1s' }}>
            <BalanceCard
              title="Saldo"
              totalAmount={metrics.projectedBalance}
              currentAmount={metrics.currentBalance}
              icon={<Wallet className="h-5 w-5" />}
              showProgress={true}
            />
          </div>

          {/* Card de Receitas - Powered by Phase 2 stored procedure */}
          <div className="animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <BalanceCard
              title="Receitas"
              totalAmount={metrics.totalIncome}
              currentAmount={metrics.totalIncome - metrics.pendingIncome}
              icon={<ArrowUpCircle className="h-5 w-5" />}
              variant="income"
              showProgress={metrics.pendingIncome > 0}
            />
          </div>

          {/* Card de Despesas - Powered by Phase 2 stored procedure */}
          <div className="animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <BalanceCard
              title="Despesas"
              totalAmount={metrics.totalExpense}
              currentAmount={metrics.totalExpense - metrics.pendingExpense}
              icon={<ArrowDownCircle className="h-5 w-5" />}
              variant="expense"
              showProgress={metrics.pendingExpense > 0}
            />
          </div>

          {/* Card de Contas Vencidas */}
          <div className="animate-slide-up" style={{ animationDelay: '0.4s' }}>
            <BalanceCard
              title="Vencidas"
              totalAmount={overdueSummary?.total_overdue_amount || 0}
              currentAmount={overdueSummary?.total_overdue_amount || 0}
              icon={<AlertTriangle className="h-5 w-5" />}
              variant="expense"
              showProgress={false}
            />
          </div>
        </div>

        <div className="animate-slide-up" style={{ animationDelay: '0.4s' }}>
          <Tabs defaultValue="overview" onValueChange={setActiveTab}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 md:mb-6">
              <TabsList className="bg-card border border-border w-full sm:w-auto">
                <TabsTrigger
                  value="overview"
                  className="relative data-[state=active]:bg-primary data-[state=active]:text-primary-foreground dark:data-[state=active]:bg-accent dark:data-[state=active]:text-accent-foreground transition-all duration-200 flex-1 sm:flex-none"
                >
                  <BarChart3 className="mr-2 h-4 w-4" />
                  Geral
                  {activeTab === "overview" && (
                    <span className="absolute -bottom-px left-0 h-0.5 w-full bg-primary rounded-full" />
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="income"
                  className="relative data-[state=active]:bg-finance-green data-[state=active]:text-white transition-all duration-200 flex-1 sm:flex-none"
                >
                  <ArrowUpCircle className="mr-2 h-4 w-4" />
                  Receitas
                  {activeTab === "income" && (
                    <span className="absolute -bottom-px left-0 h-0.5 w-full bg-finance-green rounded-full" />
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="expense"
                  className="relative data-[state=active]:bg-finance-red data-[state=active]:text-white transition-all duration-200 flex-1 sm:flex-none"
                >
                  <ArrowDownCircle className="mr-2 h-4 w-4" />
                  Despesas
                  {activeTab === "expense" && (
                    <span className="absolute -bottom-px left-0 h-0.5 w-full bg-finance-red rounded-full" />
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Date Range Filter */}
              <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal bg-card border-border hover:bg-accent/50 transition-all duration-200",
                      !dateRange.from && "text-muted-foreground"
                    )}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    <span className="capitalize">{dateRangeLabel}</span>
                    <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <div className="flex">
                    {/* Preset buttons */}
                    <div className="flex flex-col gap-1 p-3 border-r border-border bg-muted/30">
                      <p className="text-xs font-medium text-muted-foreground mb-2 px-2">Período</p>
                      {(Object.entries(getDatePresets()) as [PresetKey, DatePreset][])
                        .filter(([key]) => key !== 'custom')
                        .map(([key, preset]) => (
                          <Button
                            key={key}
                            variant={selectedPreset === key ? "default" : "ghost"}
                            size="sm"
                            className={cn(
                              "justify-start text-left h-8 px-2",
                              selectedPreset === key && "bg-primary text-primary-foreground"
                            )}
                            onClick={() => handlePresetChange(key)}
                          >
                            {preset.label}
                          </Button>
                        ))}
                    </div>
                    {/* Calendar for custom date selection */}
                    <div className="p-3">
                      <CalendarComponent
                        mode="range"
                        defaultMonth={dateRange.from}
                        selected={dateRange}
                        onSelect={handleDateRangeChange}
                        numberOfMonths={2}
                        locale={ptBR}
                        className="pointer-events-auto"
                      />
                      {dateRange.from && dateRange.to && (
                        <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">
                            {format(dateRange.from, "dd/MM/yyyy", { locale: ptBR })} - {format(dateRange.to, "dd/MM/yyyy", { locale: ptBR })}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setIsDatePickerOpen(false)}
                          >
                            Aplicar
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <TabsContent value="overview" className="mt-4 space-y-6">
              {/* Seção de Analytics por Categoria */}
              <div className="analytics-grid">
                {/* Top Categorias de Receita */}
                <div className="rounded-smooth border-0 bg-card text-card-foreground shadow-sm card-elevated overflow-hidden dark:bg-muted/50 dark:border-border/50">
                  <div className="pb-4 bg-gradient-to-r from-finance-green/5 to-emerald-500/5 p-6">
                    <h3 className="text-lg flex items-center gap-2 font-medium">
                      <TrendingUp className="h-5 w-5 text-finance-green" />
                      Top Receitas
                    </h3>
                  </div>
                  <div className="p-6">
                    {dashboardData.analytics.topIncomeCategories.length > 0 ? (
                      <div className="space-y-3">
                        {dashboardData.analytics.topIncomeCategories.map((category, index) => (
                          <div key={`income-${category.name}`} className="flex items-center justify-between p-3 rounded-lg bg-finance-green/5 border border-finance-green/10">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-finance-green/20 flex items-center justify-center text-sm font-semibold text-finance-green">
                                {index + 1}
                              </div>
                              <div>
                                <p className="font-medium text-foreground">{category.name}</p>
                                <p className="text-sm text-muted-foreground">{category.transactionCount} transações</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-finance-green">
                                {formatCurrency(category.totalAmount)}
                              </p>
                              <p className="text-sm text-muted-foreground">{category.percentage.toFixed(1)}%</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <ArrowUpCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>Nenhuma categoria de receita encontrada</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Top Categorias de Despesa */}
                <div className="rounded-smooth border-0 bg-card text-card-foreground shadow-sm card-elevated overflow-hidden dark:bg-muted/50 dark:border-border/50">
                  <div className="pb-4 bg-gradient-to-r from-finance-red/5 to-rose-500/5 p-6">
                    <h3 className="text-lg flex items-center gap-2 font-medium">
                      <TrendingUp className="h-5 w-5 text-finance-red rotate-180" />
                      Top Despesas
                    </h3>
                  </div>
                  <div className="p-6">
                    {dashboardData.analytics.topExpenseCategories.length > 0 ? (
                      <div className="space-y-3">
                        {dashboardData.analytics.topExpenseCategories.map((category, index) => (
                          <div key={`expense-${category.name}`} className="flex items-center justify-between p-3 rounded-lg bg-finance-red/5 border border-finance-red/10">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-finance-red/20 flex items-center justify-center text-sm font-semibold text-finance-red">
                                {index + 1}
                              </div>
                              <div>
                                <p className="font-medium text-foreground">{category.name}</p>
                                <p className="text-sm text-muted-foreground">{category.transactionCount} transações</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-finance-red">
                                {formatCurrency(category.totalAmount)}
                              </p>
                              <p className="text-sm text-muted-foreground">{category.percentage.toFixed(1)}%</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <ArrowDownCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>Nenhuma categoria de despesa encontrada</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Comparação Mensal */}
              {dashboardData.analytics.monthlyComparison && (
                <div className="rounded-smooth border-0 bg-card text-card-foreground shadow-sm card-elevated overflow-hidden dark:bg-muted/50 dark:border-border/50 mb-6">
                  <div className="pb-4 bg-gradient-to-r from-blue-500/5 to-indigo-500/5 p-6">
                    <h3 className="text-lg flex items-center gap-2 font-medium">
                      <BarChart3 className="h-5 w-5 text-blue-500" />
                      Mensal
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-2">Mês Anterior</p>
                        <p className="text-2xl font-bold text-foreground">
                          {formatCurrency(dashboardData.analytics.monthlyComparison.previousMonth)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-2">Mês Atual</p>
                        <p className="text-2xl font-bold text-primary">
                          {formatCurrency(dashboardData.analytics.monthlyComparison.currentMonth)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-2">Variação</p>
                        <div className={`flex items-center justify-center gap-2 ${dashboardData.analytics.monthlyComparison.change >= 0 ? 'text-finance-green' : 'text-finance-red'}`}>
                          {dashboardData.analytics.monthlyComparison.change >= 0 ? (
                            <ArrowUpCircle className="h-5 w-5" />
                          ) : (
                            <ArrowDownCircle className="h-5 w-5" />
                          )}
                          <span className="text-xl font-bold">
                            {dashboardData.analytics.monthlyComparison.changePercentage >= 0 ? '+' : ''}{dashboardData.analytics.monthlyComparison.changePercentage.toFixed(1)}%
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {dashboardData.analytics.monthlyComparison.change >= 0 ? 'Aumento' : 'Redução'} de {Math.abs(dashboardData.analytics.monthlyComparison.change).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-smooth border-0 bg-card text-card-foreground shadow-sm card-elevated overflow-hidden dark:bg-muted/50 dark:border-border/50">
                <div className="pb-4 bg-gradient-to-r from-primary/5 to-accent/5 p-6">
                  <h3 className="text-xl flex items-center gap-2 font-medium">
                    <BarChart3 className="h-5 w-5 text-primary" />
                    Caixa
                  </h3>
                </div>
                <div className="p-6">
                  <div className="dashboard-chart-container">
                    {transactions ? (
                      <CashFlowChart transactions={transactions} />
                    ) : (
                      <div className="h-[300px] flex items-center justify-center">
                        <div className="text-center text-muted-foreground animate-pulse">
                          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                            <BarChart3 className="h-8 w-8" />
                          </div>
                          <p className="text-sm">Carregando dados do gráfico...</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-foreground">Transações</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/transactions")}
                  className="text-primary hover:text-primary/80"
                >
                  Ver todas
                </Button>
              </div>

              <ScrollArea className="h-[400px] rounded-lg border-0 bg-card/50">
                <div className="space-y-3 p-4">
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="skeleton h-16 w-full" />
                    ))
                  ) : dashboardData.recentTransactions.length > 0 ? (
                    dashboardData.recentTransactions.map((transaction, index) => (
                      <div
                        key={transaction.id}
                        className="animate-slide-up"
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <TransactionCard
                          transaction={transaction}
                          onEdit={handleEditTransaction}
                          onDelete={handleDeleteTransaction}
                          onTogglePaid={handleTogglePaid}
                          isSelected={selectedTransactions.has(transaction.id)}
                          onSelect={handleSelectTransaction}
                          allTransactions={transactions}
                        />
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                        <Plus className="h-10 w-10 text-muted-foreground" />
                      </div>
                      <p className="text-muted-foreground mb-4">Nenhuma transação encontrada.</p>
                      <Button
                        className="btn-primary animate-bounce-subtle"
                        onClick={() => setIsDialogOpen(true)}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Adicionar Primeira Transação
                      </Button>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="income" className="mt-4 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-finance-green flex items-center gap-2">
                  <ArrowUpCircle className="h-5 w-5" />
                  Receitas
                </h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="w-2 h-2 bg-finance-green rounded-full"></span>
                  {dashboardData.incomeTransactions.length} receitas
                </div>
              </div>

              <ScrollArea className="h-[500px] rounded-lg border-0 bg-card/50">
                <div className="space-y-3 p-4">
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="skeleton h-16 w-full" />
                    ))
                  ) : dashboardData.incomeTransactions.length > 0 ? (
                    dashboardData.incomeTransactions.map((transaction, index) => (
                      <div
                        key={transaction.id}
                        className="animate-slide-up"
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <TransactionCard
                          transaction={transaction}
                          onEdit={handleEditTransaction}
                          onDelete={handleDeleteTransaction}
                          onTogglePaid={handleTogglePaid}
                          isSelected={selectedTransactions.has(transaction.id)}
                          onSelect={handleSelectTransaction}
                          allTransactions={transactions}
                        />
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-finance-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <ArrowUpCircle className="h-10 w-10 text-finance-green" />
                      </div>
                      <p className="text-muted-foreground mb-4">Nenhuma receita encontrada.</p>
                      <Button
                        className="bg-finance-green hover:bg-finance-green/90 text-white"
                        onClick={() => setIsDialogOpen(true)}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Adicionar Receita
                      </Button>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="expense" className="mt-4 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-finance-red flex items-center gap-2">
                  <ArrowDownCircle className="h-5 w-5" />
                  Despesas
                </h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="w-2 h-2 bg-finance-red rounded-full"></span>
                  {dashboardData.expenseTransactions.length} despesas
                </div>
              </div>

              <ScrollArea className="h-[500px] rounded-lg border-0 bg-card/50">
                <div className="space-y-3 p-4">
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="skeleton h-16 w-full" />
                    ))
                  ) : dashboardData.expenseTransactions.length > 0 ? (
                    dashboardData.expenseTransactions.map((transaction, index) => (
                      <div
                        key={transaction.id}
                        className="animate-slide-up"
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <TransactionCard
                          transaction={transaction}
                          onEdit={handleEditTransaction}
                          onDelete={handleDeleteTransaction}
                          onTogglePaid={handleTogglePaid}
                          isSelected={selectedTransactions.has(transaction.id)}
                          onSelect={handleSelectTransaction}
                          allTransactions={transactions}
                        />
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-finance-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <ArrowDownCircle className="h-10 w-10 text-finance-red" />
                      </div>
                      <p className="text-muted-foreground mb-4">Nenhuma despesa encontrada.</p>
                      <Button
                        className="bg-finance-red hover:bg-finance-red/90 text-white"
                        onClick={() => setIsDialogOpen(true)}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Adicionar Despesa
                      </Button>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>

        <UnifiedTransactionForm
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
          transaction={editingTransactionId ? transactions?.find(t => t.id === editingTransactionId) : undefined}
          isEditing={!!editingTransactionId}
          onSave={async (data) => {
            await dashboardActions.handleSaveTransaction(data, editingTransactionId, handleCloseDialog);
            if (editingTransactionId) {
              toast.success("Transação atualizada com sucesso!");
            } else {
              toast.success("Transação criada com sucesso!");
            }
          }}
        />
      </div>
    </div>
  );
}
